import { useState, useRef, useCallback, useEffect } from 'react'

export type RecorderState = 'idle' | 'recording' | 'paused' | 'stopped'

interface RecorderHook {
  state: RecorderState
  duration: number
  fileSize: number
  mimeType: string
  audioLevel: number
  blob: Blob | null
  error: string | null
  isSupported: boolean
  start: () => Promise<void>
  pause: () => void
  resume: () => void
  stop: () => void
  reset: () => void
}

const MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/wav'
]

const MAX_DURATION_MS = 5 * 60 * 1000 // 5 minutes
const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20 MB

export function useRecorder(): RecorderHook {
  const [state, setState] = useState<RecorderState>('idle')
  const [duration, setDuration] = useState(0)
  const [fileSize, setFileSize] = useState(0)
  const [mimeType, setMimeType] = useState('')
  const [audioLevel, setAudioLevel] = useState(0)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSupported, setIsSupported] = useState(true)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const startTimeRef = useRef<number>(0)
  const animationFrameRef = useRef<number>(0)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)

  // Check browser support
  useEffect(() => {
    const supported = typeof MediaRecorder !== 'undefined' && 
                     MIME_TYPES.some(type => MediaRecorder.isTypeSupported(type))
    setIsSupported(supported)
  }, [])

  // Get supported MIME type
  const getSupportedMimeType = useCallback((): string => {
    return MIME_TYPES.find(type => MediaRecorder.isTypeSupported(type)) || 'audio/webm'
  }, [])

  // Get file extension from MIME type
  const getExtension = useCallback((mime: string): string => {
    if (mime.includes('webm')) return 'webm'
    if (mime.includes('mp4')) return 'mp4'
    if (mime.includes('wav')) return 'wav'
    return 'webm'
  }, [])

  // Audio level monitoring
  const updateAudioLevel = useCallback(() => {
    if (!analyserRef.current || state !== 'recording') {
      setAudioLevel(0)
      return
    }

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount)
    analyserRef.current.getByteFrequencyData(dataArray)
    
    const sum = dataArray.reduce((acc, val) => acc + val, 0)
    const average = sum / dataArray.length
    const level = Math.min(average / 128, 1) // Normalize to 0-1

    setAudioLevel(level)
    animationFrameRef.current = requestAnimationFrame(updateAudioLevel)
  }, [state])

  // Duration tracking
  useEffect(() => {
    let interval: number | undefined

    if (state === 'recording') {
      interval = window.setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current
        setDuration(elapsed)

        // Auto-stop at max duration
        if (elapsed >= MAX_DURATION_MS) {
          stop()
        }
      }, 100)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [state])

  const cleanup = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close()
      audioContextRef.current = null
    }
    
    analyserRef.current = null
    mediaRecorderRef.current = null
  }, [])

  const start = useCallback(async () => {
    try {
      setError(null)
      
      if (!isSupported) {
        throw new Error('Audio recording is not supported in this browser')
      }

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      })
      
      streamRef.current = stream

      // Set up audio analysis
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      if (audioContext.state === 'suspended') {
        await audioContext.resume()
      }
      
      audioContextRef.current = audioContext
      const source = audioContext.createMediaStreamSource(stream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      analyserRef.current = analyser

      // Set up MediaRecorder
      const supportedMime = getSupportedMimeType()
      setMimeType(supportedMime)
      
      const recorder = new MediaRecorder(stream, { 
        mimeType: supportedMime,
        audioBitsPerSecond: 96000 // Good quality, reasonable size
      })
      
      chunksRef.current = []
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
          
          // Estimate file size
          const totalSize = chunksRef.current.reduce((sum, chunk) => sum + chunk.size, 0)
          setFileSize(totalSize)
          
          // Check size limit
          if (totalSize > MAX_FILE_SIZE) {
            stop()
            setError('Recording stopped: file size limit (20MB) reached')
          }
        }
      }
      
      recorder.onstop = () => {
        const recordedBlob = new Blob(chunksRef.current, { type: supportedMime })
        setBlob(recordedBlob)
        setFileSize(recordedBlob.size)
      }
      
      recorder.onerror = (event) => {
        setError('Recording error occurred')
        console.error('MediaRecorder error:', event)
      }

      mediaRecorderRef.current = recorder
      startTimeRef.current = Date.now()
      
      recorder.start(100) // Collect data every 100ms
      setState('recording')
      
      // Start audio level monitoring
      updateAudioLevel()
      
    } catch (err: any) {
      setError(err.message || 'Failed to start recording')
      cleanup()
    }
  }, [isSupported, getSupportedMimeType, updateAudioLevel, cleanup])

  const pause = useCallback(() => {
    if (mediaRecorderRef.current && state === 'recording') {
      mediaRecorderRef.current.pause()
      setState('paused')
      setAudioLevel(0)
    }
  }, [state])

  const resume = useCallback(() => {
    if (mediaRecorderRef.current && state === 'paused') {
      mediaRecorderRef.current.resume()
      setState('recording')
      updateAudioLevel()
    }
  }, [state, updateAudioLevel])

  const stop = useCallback(() => {
    if (mediaRecorderRef.current && (state === 'recording' || state === 'paused')) {
      mediaRecorderRef.current.stop()
      setState('stopped')
      setAudioLevel(0)
      
      // Stop monitoring
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [state])

  const reset = useCallback(() => {
    cleanup()
    setState('idle')
    setDuration(0)
    setFileSize(0)
    setAudioLevel(0)
    setBlob(null)
    setError(null)
    chunksRef.current = []
  }, [cleanup])

  // Cleanup on unmount
  useEffect(() => {
    return cleanup
  }, [cleanup])

  return {
    state,
    duration,
    fileSize,
    mimeType,
    audioLevel,
    blob,
    error,
    isSupported,
    start,
    pause,
    resume,
    stop,
    reset
  }
}